import {Component, computed, inject, Injector, input, output, viewChild, ChangeDetectionStrategy} from "@angular/core"
import {toObservable, toSignal} from "@angular/core/rxjs-interop"
import {narrowInstance} from "@cm/utils"
import {MatDialog} from "@angular/material/dialog"
import {MatMenuModule} from "@angular/material/menu"
import {MatTooltipModule} from "@angular/material/tooltip"
import {UploadGqlService} from "@app/common/services/upload/upload.gql.service"
import {getNodeIconSecondaryClass} from "@app/template-editor/helpers/template-icons"
import {selectFile, uploadImage} from "@app/template-editor/helpers/upload"
import {TemplateNodeDragService} from "@app/template-editor/services/template-node-drag.service"
import {DataObjectReference, ImageInput, ImageLikeNode} from "@cm/template-nodes"
import {CardComponent} from "@common/components/cards/card/card.component"
import {DataObjectThumbnailComponent} from "@common/components/data-object-thumbnail/data-object-thumbnail.component"
import {ThumbnailComponent} from "@common/components/thumbnail/thumbnail.component"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {Settings} from "@common/models/settings/settings"
import {GetDataObjectForImageInspectorGQL} from "@template-editor/components/inspectors/image-inspector/image-inspector.generated"
import {catchError, from, of, switchMap} from "rxjs"
import {TemplateNodeComponent} from "@app/template-editor/components/template-node/template-node.component"
import {BaseInspectorComponent} from "@app/template-editor/components/inspectors/base-inspector/base-inspector.component"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-image-inspector",
    templateUrl: "./image-inspector.component.html",
    styleUrls: ["./image-inspector.component.scss", "./../../../helpers/template-icons.scss"],
    imports: [CardComponent, TemplateNodeComponent, MatMenuModule, MatTooltipModule, DataObjectThumbnailComponent, ThumbnailComponent],
})
export class ImageInspectorComponent extends BaseInspectorComponent<ImageLikeNode | null> {
    readonly $label = input.required<string | undefined>({alias: "label"})
    readonly $labelText = computed(() => {
        const label = this.$label()
        if (label && label.length > 0) return `${label}: `
        return "Image: "
    })
    readonly $highlighted = input(false, {alias: "highlighted"})
    readonly requestUpdateImage = output<ImageLikeNode | null>()
    private readonly $dragImage = viewChild.required<TemplateNodeComponent>("dragImage")

    readonly injector = inject(Injector)
    private readonly dataObjectGql = inject(GetDataObjectForImageInspectorGQL)

    private readonly uploadService = inject(UploadGqlService)
    private readonly dialog = inject(MatDialog)
    readonly drag = inject(TemplateNodeDragService)

    readonly $imageIcon = computed(() => getNodeIconSecondaryClass(ImageInput.getNodeClass()))

    readonly $dataObjectReference = computed(() => narrowInstance(this.$node(), DataObjectReference))

    private readonly $thumbnailData = toSignal(
        toObservable(this.$node).pipe(
            switchMap((node) => {
                if (node instanceof DataObjectReference) return from(fetchThrowingErrors(this.dataObjectGql)({legacyId: node.parameters.dataObjectId}))
                else return of(Settings.SWITCH_MATERIAL_URL)
            }),
            catchError(() => of(Settings.IMAGE_NOT_AVAILABLE_SMALL_URL)),
        ),
        {
            initialValue: Settings.IMAGE_NOT_AVAILABLE_SMALL_URL,
        },
    )

    readonly $thumbnailUrl = computed(() => {
        const thumbnailData = this.$thumbnailData()
        if (typeof thumbnailData === "string") return thumbnailData
        else return undefined
    })
    readonly $thumbnailObject = computed(() => {
        const thumbnailData = this.$thumbnailData()
        if (typeof thumbnailData === "string") return undefined
        return thumbnailData.dataObject
    })

    readonly $isReference = computed(() => {
        const node = this.$node()
        if (!node) return false
        return !(node.parents.size === 1)
    })

    async onClick() {
        const file = await selectFile("Select an image file for upload", ".jpeg,.jpg,.png,.tiff,.exr")
        if (file) await this.uploadImageFromFile(file)
    }

    gotoReference() {
        if (!this.$isReference()) return

        const node = this.$node()
        if (!node) return

        this.sceneManagerService.selectNode({path: [node], part: "root"})
    }

    removeAssignment() {
        this.requestUpdateImage.emit(null)
    }

    dragStart(event: DragEvent) {
        const node = this.$node()
        if (!node) {
            event.preventDefault()
            return
        }

        event.dataTransfer?.setDragImage(this.$dragImage().getDragImage(), 0, 0)

        this.drag.dragStart(event, node)
    }

    async drop(event: DragEvent) {
        const image = this.getValidImageFileFromDropEvent(event)
        if (image) {
            event.preventDefault()

            await this.uploadImageFromFile(image)
            return
        }
    }

    async uploadImageFromFile(file: File) {
        const image = await uploadImage(this.injector, this.uploadService, this.sceneManagerService, this.dialog, file)
        if (!image) return
        this.requestUpdateImage.emit(image)
    }

    getValidImageFileFromDropEvent(event: DragEvent) {
        if (!event.dataTransfer?.types?.includes("Files")) return undefined
        if (event.dataTransfer.files.length !== 1) return undefined
        const file = event.dataTransfer.files[0]
        return ["image/jpeg", "image/png", "image/tiff", "image/x-exr"].includes(file.type) ? file : undefined
    }
}
